import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Avatar, Box, Button, Divider, Stack, Typography } from "@mui/material";
import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "./Translate";
import PublicPageLayout, { useAuthUser } from "../components/PublicPageLayout";
import ThreadLinkCards from "../components/ThreadLinkCards";
import { toJsDate } from "./eventsUtils";
import { brandColors } from "../theme";
import useAuthorPhotos from "./useAuthorPhotos";

const formatWhen = (value) => {
  const d = toJsDate(value);
  return d ? d.toLocaleDateString() : "";
};

const Author = ({ name, image, when }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Avatar src={image || undefined} alt={name}>
      {(name || "?").charAt(0)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
      {when && <Typography variant="caption" sx={{ color: "text.secondary" }}>{when}</Typography>}
    </Box>
  </Stack>
);

// Ficha pública de un hilo (/hilos/:threadId) con sus comentarios: se ve sin
// iniciar sesión. Un repost muestra el hilo original. Para comentar o
// reaccionar se va a /hilos (con sesión).
const ThreadDetail = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checked, user } = useAuthUser();
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const authorPhotos = useAuthorPhotos([thread?.uid, ...comments.map((c) => c.uid)]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let snap = await getDoc(doc(db, "threads", threadId));
        if (snap.exists() && snap.data().repostOf) {
          snap = await getDoc(doc(db, "threads", snap.data().repostOf));
        }
        if (!active) return;
        if (!snap.exists()) {
          setThread(null);
          return;
        }
        setThread({ id: snap.id, ...snap.data() });
        const commentsSnap = await getDocs(
          query(collection(db, "threadComments"), where("threadId", "==", snap.id), orderBy("createdAt", "asc"))
        );
        if (active) setComments(commentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error al obtener el hilo:", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [threadId]);

  const images = Array.isArray(thread?.images) ? thread.images.filter(Boolean) : [];

  const handleJoin = () => {
    if (user) navigate("/hilos");
    else navigate("/login", { state: { from: location } });
  };

  return (
    <PublicPageLayout
      title={<T>Hilo</T>}
      subtitle={thread?.name || "Notiline"}
      loading={loading || !checked}
      notFound={!thread && <T>No se encontró el hilo.</T>}
      isGuest={!user}
      showAd
    >
      <Stack spacing={2.5}>
        <Author name={thread?.name} image={authorPhotos[thread?.uid] || thread?.image} when={formatWhen(thread?.createdAt)} />

        {thread?.text && (
          <Typography component="h1" sx={{ fontSize: 18, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
            {thread.text}
          </Typography>
        )}

        <ThreadLinkCards links={thread?.links} />

        {images.length > 0 && (
          <Stack spacing={1}>
            {images.map((src, i) => (
              <Box
                key={src}
                component="img"
                src={src}
                alt={`Imagen ${i + 1} del hilo`}
                loading="lazy"
                sx={{ width: "100%", maxHeight: 480, objectFit: "contain", borderRadius: 2, bgcolor: brandColors.backgroundDefault }}
              />
            ))}
          </Stack>
        )}

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {(thread?.likes?.length || 0)} <T>me gusta</T> · {comments.length} <T>comentarios</T>
        </Typography>

        <Button
          variant="contained"
          onClick={handleJoin}
          sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600 }}
        >
          {user ? <T>Ir a Hilos</T> : <T>Iniciá sesión para responder</T>}
        </Button>

        {comments.length > 0 && (
          <>
            <Divider />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              <T>Comentarios</T>
            </Typography>
            <Stack spacing={2}>
              {comments.map((c) => (
                <Box key={c.id}>
                  <Author name={c.name} image={authorPhotos[c.uid] || c.image} when={formatWhen(c.createdAt)} />
                  <Typography sx={{ mt: 1, pl: 7, whiteSpace: "pre-line", overflowWrap: "anywhere", color: "text.secondary" }}>
                    {c.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </PublicPageLayout>
  );
};

export default ThreadDetail;
